import axios, { AxiosRequestConfig } from 'axios';
import { ERROR_CATALOG, ErrorCode, MappedError } from '@/api/errorCatalog';

export interface ApiErrorResponse {
  code: ErrorCode;
  message?: string;
  details?: Record<string, any>;
  statusCode: number;
}

export class ApiError extends Error {
  code: ErrorCode;
  statusCode: number;
  details?: Record<string, any>;
  mappedError: MappedError;

  constructor(
    code: ErrorCode,
    statusCode: number,
    details?: Record<string, any>
  ) {
    const errorConfig = ERROR_CATALOG[code] || ERROR_CATALOG.DEFAULT;

    super(errorConfig.message);
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
    this.mappedError = {
      code,
      message: errorConfig.message,
      severity: errorConfig.severity
    };
    this.name = 'ApiError';
  }
}

export async function apiClient<T>(
  endpoint: string,
  options?: AxiosRequestConfig
): Promise<T> {
  const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
  try {
    const response = await axios({
      url: `${BASE_URL}${endpoint}`,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers || {})
      }
    });
    return response.data as T;
  } catch (error: any) {
    if (error.response) {
      // Backend responded with error status
      const { status, data } = error.response;
      const errorCode: ErrorCode = data?.code || 'CL_INTERNAL_SERVER_ERROR';

      throw new ApiError(errorCode, status, data?.details);
    } else if (error.request) {
      throw new ApiError('CL_INTERNAL_SERVER_ERROR', 0, {
        originalError: 'No response from server'
      });
    } else {
      throw new ApiError('CL_INTERNAL_SERVER_ERROR', 0, {
        originalError: error.message
      });
    }
  }
}
